import { useEffect, useRef, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Loader2, CheckCircle, XCircle } from 'lucide-react'
import api from '@/services/api'

/**
 * Stripe redirects the customer here after a payment that needs a redirect step
 * (3-D Secure / bank authentication). Stripe appends `payment_intent`,
 * `payment_intent_client_secret` and `redirect_status` to the URL.
 *
 * Without this page the redirect landed on a dead route, the on-page confirm never
 * ran, and the payment sat on "processing" forever. Here we hand the PaymentIntent id
 * to the backend, which re-verifies it with Stripe and finalizes it (top-up credit /
 * Pay&Go cycle, or order paid) idempotently — the same work the webhook does.
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const ran = useRef(false)
  const [state, setState] = useState<'working' | 'done' | 'pending' | 'failed'>('working')

  const paymentIntent = params.get('payment_intent')
  const redirectStatus = params.get('redirect_status')

  useEffect(() => {
    if (ran.current) return
    ran.current = true

    let cancelled = false
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

    ;(async () => {
      if (!paymentIntent) {
        // Hit directly with no Stripe params — treat as a soft failure.
        setState(redirectStatus === 'succeeded' ? 'done' : 'failed')
        return
      }

      // Async methods (Klarna / Revolut Pay / 3-D Secure) can land here a beat before Stripe
      // finishes settling — status is momentarily 'processing'. That is NOT a failure and NOT
      // a minutes-long wait: it settles in seconds. So we POLL (re-verifying against Stripe
      // each time) until it's terminal, instead of showing a one-shot "processing" screen the
      // customer would have to refresh. ~2s apart, up to ~40s; the webhook is finalising it in
      // parallel, so this almost always flips to 'done' within a few seconds.
      const deadline = Date.now() + 40_000
      let attempt = 0
      while (!cancelled) {
        try {
          const { data } = await api.post('/payments/finalize-by-intent', {
            payment_intent_id: paymentIntent,
          })
          if (data?.completed || data?.status === 'succeeded') { setState('done'); return }
          // A gateway-confirmed terminal failure — stop and tell them plainly.
          if (['canceled', 'requires_payment_method'].includes(data?.status)) { setState('failed'); return }
          // else still in-flight (processing / requires_action / requires_capture) → keep polling.
        } catch {
          // Transient (network / backend blip) — keep polling; the webhook is the backstop.
        }
        if (Date.now() >= deadline) {
          // Still settling after the polling window. NOT charged-and-lost: the webhook /
          // reconcile will complete it. Reassure rather than alarm.
          if (!cancelled) setState('pending')
          return
        }
        attempt += 1
        await sleep(attempt <= 3 ? 1500 : 2500)
      }
    })()

    return () => { cancelled = true }
  }, [paymentIntent, redirectStatus])

  return (
    <div className="max-w-md mx-auto px-4 py-20 text-center">
      {state === 'working' && (
        <>
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-primary-600" />
          <p className="text-gray-600">Confirming your payment…</p>
        </>
      )}

      {state === 'done' && (
        <>
          <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Payment received</h1>
          <p className="text-gray-600 mb-6">Thank you — your payment was successful and your account has been updated.</p>
          <button
            onClick={() => navigate('/account')}
            className="px-5 py-2.5 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700"
          >
            Go to my account
          </button>
        </>
      )}

      {state === 'pending' && (
        <>
          <Loader2 className="w-12 h-12 mx-auto mb-4 text-amber-500" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Payment processing</h1>
          <p className="text-gray-600 mb-6">
            Your payment is being processed. This can take a minute — your account will update automatically once it
            clears. You can safely close this page.
          </p>
          <button
            onClick={() => navigate('/account')}
            className="px-5 py-2.5 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700"
          >
            Go to my account
          </button>
        </>
      )}

      {state === 'failed' && (
        <>
          <XCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Payment not completed</h1>
          <p className="text-gray-600 mb-6">
            We couldn't confirm your payment, so nothing has been charged. Please try again, and if you were charged,
            contact support.
          </p>
          <button
            onClick={() => navigate('/account')}
            className="px-5 py-2.5 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700"
          >
            Back to my account
          </button>
        </>
      )}
    </div>
  )
}
